import {
  cmToInches,
  createPptx,
  formatNumberWithSuffix,
  generateSourceText,
  getMetricValueShift,
  getImageUrl,
  getSourceTextBox,
  hasData,
  inchesToCm,
  layoutMetricGroupsByBoundsRightToLeft,
  TRANSLATIONS,
} from "./utils";

function mapBrandToTemplateData(brand, lang = "fr") {
  const report = brand.report || {};
  return {
    id: brand.id,
    author: brand.author || "",
    job: (lang === 'en' && brand.author_occupation_en) ? brand.author_occupation_en : (brand.author_occupation || ""),
    title: (lang === 'en' && brand.title_en) ? brand.title_en : (brand.title || ""),
    baseline: (lang === 'en' && brand.baseline_en) ? brand.baseline_en : (brand.baseline || ""),
    target: (lang === 'en' && brand.positioning_en) ? brand.positioning_en : (brand.positioning || ""),
    author_image_path: brand.author_image_path,
    presentation_image_path: brand.presentation_image_path,
    social_image_path: brand.social_image_path,
    audience_brand: report.audience_brand || 0,
    print_readers: report.audience_print_readers || 0,
    print_copies: report.diffusion_print || 0,
    digital_visitors: report.audience_digital || 0,
    social_followers: report.social_data || 0,
    audio_listeners: report.audio_data || 0,
    newsletter_subscribers: report.newsletter_data || 0,
  };
}

export async function generateTemplate3(brand, sourceSettings = {}, lang = "fr") {
  const data = mapBrandToTemplateData(brand, lang);
  const pptx = createPptx(brand.brand_name);
  const slide = pptx.addSlide();
  const reportData = brand.report || {};
  const t = TRANSLATIONS[lang];
  const sourceText = generateSourceText(3, sourceSettings, reportData, lang);
  const pageWidthCm = inchesToCm(13.333);
  const metricLayoutConfig = {
    zoneRightCm: pageWidthCm - 0.5,
    gapCm: 0.03,
    separatorGapCm: 0.18,
  };

  const bgUrl = getImageUrl("marketing/orange_bg.png");

  slide.addImage({
    path: bgUrl,
    x: cmToInches(7.16),
    y: cmToInches(-2.26),
    w: cmToInches(21.81),
    h: cmToInches(23),
    rotate: 353,
  });

  slide.addShape("rect", {
    x: 0,
    y: 0,
    w: 13.33,
    h: 7.5,
    fill: { type: "solid", color: "FFFFFF", transparency: 25 },
    line: { type: "none" },
  });

  slide.addShape("rect", {
    x: 0,
    y: 0,
    w: 13.33,
    h: 7.5,
    fill: { type: "none" },
    line: {
      color: "D77423",
      width: 8,
    },
  });

  // add reworld media connect logo
  slide.addImage({
    path: getImageUrl("marketing/reworld_media_connect_logo.png"),
    h: cmToInches(0.72),
    w: cmToInches(2.93),
    x: cmToInches(0.56),
    y: cmToInches(17.83),
  });

  const { number: digitalVisitorsNum, suffix: digitalVisitorsSuffix } =
    formatNumberWithSuffix(data.digital_visitors, lang);
  const { number: socialFollowersNum, suffix: socialFollowersSuffix } =
    formatNumberWithSuffix(data.social_followers, lang);
  const { number: audioListenersNum, suffix: audioListenersSuffix } =
    formatNumberWithSuffix(data.audio_listeners, lang);
  const {
    number: newsletterSubscribersNum,
    suffix: newsletterSubscribersSuffix,
  } = formatNumberWithSuffix(data.newsletter_subscribers, lang);
  const socialValueShiftCm = getMetricValueShift("template3", "social", socialFollowersNum);
  const newsletterValueShiftCm = getMetricValueShift(
    "template3",
    "newsletter",
    newsletterSubscribersNum
  );
  const digitalValueShiftCm = getMetricValueShift("template3", "digital", digitalVisitorsNum);
  const podcastValueShiftCm = getMetricValueShift("template3", "podcast", audioListenersNum);

  const fontName = "Montserrat, Arial";
  const orangeColor = "D77423";

  const renderMetricSeparator = (group) => {
    if (group.separatorXCm === null) {
      return;
    }

    slide.addShape("line", {
      x: cmToInches(group.separatorXCm),
      y: cmToInches(group.separatorY),
      w: cmToInches(0),
      h: cmToInches(group.separatorHCm),
      line: {
        color: "#000000",
        width: 0.75,
      },
    });
  };

  const renderSingleMetricGroup = (group) => {
    slide.addText(
      [
        {
          text: group.title,
          options: {
            fontSize: 18,
            bold: true,
            color: "000000",
            fontFace: fontName,
            align: "center",
          },
        },
      ],
      {
        h: cmToInches(group.titleBox.hCm),
        w: cmToInches(group.titleBox.wCm),
        x: cmToInches(group.anchorXCm + group.titleBox.offsetXCm),
        y: cmToInches(group.titleBox.yCm),
        valign: "top",
        halign: "left",
      }
    );

    slide.addText(
      [
        {
          text: group.number,
          options: {
            fontSize: 36,
            bold: true,
            color: "000000",
            fontFace: fontName,
            align: "right",
          },
        },
      ],
      {
        h: cmToInches(group.numberBox.hCm),
        w: cmToInches(group.numberBox.wCm),
        x: cmToInches(group.anchorXCm + group.numberBox.offsetXCm),
        y: cmToInches(group.numberBox.yCm),
        valign: "top",
        halign: "left",
      }
    );

    if (group.suffix) {
      slide.addText(
        [
          {
            text: group.suffix,
            options: {
              fontSize: 18,
              bold: true,
              color: "000000",
              fontFace: fontName,
              align: "left",
            },
          },
        ],
        {
          h: cmToInches(group.suffixBox.hCm),
          w: cmToInches(group.suffixBox.wCm),
          x: cmToInches(group.anchorXCm + group.suffixBox.offsetXCm),
          y: cmToInches(group.suffixBox.yCm),
          valign: "top",
          halign: "left",
        }
      );
    }

    slide.addText(
      [
        {
          text: group.unit,
          options: {
            fontSize: 10.5,
            bold: false,
            color: "000000",
            fontFace: fontName,
            align: "left",
          },
        },
      ],
      {
        h: cmToInches(group.unitBox.hCm),
        w: cmToInches(group.unitBox.wCm),
        x: cmToInches(group.anchorXCm + group.unitBox.offsetXCm),
        y: cmToInches(group.unitBox.yCm),
        valign: "top",
        halign: "left",
      }
    );

    renderMetricSeparator(group);
  };
  // author image
  if (data.author_image_path) {
    slide.addImage({
      path: getImageUrl(data.author_image_path),
      x: cmToInches(17.42),
      y: cmToInches(1.24),
      w: cmToInches(2.52),
      h: cmToInches(2.52),
    });
  }

  // presentation image
  slide.addImage({
    path: getImageUrl(data.presentation_image_path),
    h: cmToInches(10.87),
    w: cmToInches(8.29),
    x: cmToInches(1.29),
    y: cmToInches(1.16),
  });

  // social image
  slide.addImage({
    path: getImageUrl(data.social_image_path),
    h: cmToInches(11.17),
    w: cmToInches(5.41),
    x: cmToInches(11.03),
    y: cmToInches(0.96),
  });

  // source
  slide.addText(
    [
      {
        text: sourceText,
        options: {
          fontSize: 8,
          bold: false,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    getSourceTextBox()
  );

  slide.addText(
    [
      {
        text: data.author,
        options: {
          fontSize: 24,
          bold: true,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    {
      h: cmToInches(0.9),
      w: cmToInches(15.29),
      x: cmToInches(20.47),
      y: cmToInches(1.47),
      valign: "top",
      halign: "left",
    }
  );

  slide.addText(
    [
      {
        text: data.job,
        options: {
          fontSize: 18,
          bold: false,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    {
      x: cmToInches(20.56),
      y: cmToInches(2.48),
      w: cmToInches(10.97),
      h: cmToInches(1.53),
      valign: "top",
      halign: "left",
    }
  );

  // title
  slide.addText(
    [
      {
        text: data.title,
        options: {
          fontSize: 16,
          bold: true,
          color: "ffffff",
          fontFace: fontName,
        },
      },
    ],
    {
      x: cmToInches(17.39),
      y: cmToInches(4.64),
      w: cmToInches(13.66),
      h: cmToInches(1),
      highlight: orangeColor,
      valign: "top",
      halign: "left",
    }
  );

  // baseline
  slide.addText(
    [
      {
        text: data.baseline,
        options: {
          fontSize: 12,
          bold: true,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    {
      x: cmToInches(17.4),
      y: cmToInches(6.35),
      w: cmToInches(15),
      h: cmToInches(1),
      valign: "top",
      halign: "left",
    }
  );

  // target audience
  slide.addText(
    [
      {
        text: data.target,
        options: {
          fontSize: 12,
          bold: false,
          color: "000000",
          fontFace: fontName,
          lineSpacing: 12,
        },
      },
    ],
    {
      x: cmToInches(17.4),
      y: cmToInches(7.2),
      w: cmToInches(14.37),
      h: cmToInches(4.87),
      valign: "top",
      halign: "left",
    }
  );

  const metricGroupDefinitions = [
    {
      id: "podcast",
      visible: hasData(data.audio_listeners),
      title: "PODCAST",
      number: audioListenersNum,
      suffix: audioListenersSuffix,
      unit: t.PODCAST_IMPRESSIONS,
      titleBox: { offsetXCm: 0, yCm: 13.23, wCm: 7.59, hCm: 0.89 },
      numberBox: { offsetXCm: -1.12 + podcastValueShiftCm, yCm: 15.2, wCm: 5.23, hCm: 1.8 },
      suffixBox: { offsetXCm: 3.83 + podcastValueShiftCm, yCm: 15.33, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 3.83 + podcastValueShiftCm, yCm: 15.99, wCm: 3.37, hCm: 1.15 },
      leftNeighborAnchorStepCm: 7.29,
      separatorOffsetXCm: 0,
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
    {
      id: "newsletter",
      visible: hasData(data.newsletter_subscribers),
      title: "NEWSLETTER",
      number: newsletterSubscribersNum,
      suffix: newsletterSubscribersSuffix,
      unit: t.ABONNES_NEWSLETTER,
      titleBox: { offsetXCm: -1.21, yCm: 13.23, wCm: 7.77, hCm: 0.89 },
      numberBox: { offsetXCm: -2 + newsletterValueShiftCm, yCm: 15.17, wCm: 4.68, hCm: 1.8 },
      suffixBox: { offsetXCm: 2.25 + newsletterValueShiftCm, yCm: 15.31, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 2.23 + newsletterValueShiftCm, yCm: 15.96, wCm: 3.37, hCm: 1.15 },
      leftNeighborAnchorStepCm: 6.11,
      separatorOffsetXCm: 0,
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
    {
      id: "social",
      visible: hasData(data.social_followers),
      title: "SOCIAL",
      number: socialFollowersNum,
      suffix: socialFollowersSuffix,
      unit: t.ABONNES_RS,
      titleBox: { offsetXCm: -0.8, yCm: 13.23, wCm: 7.74, hCm: 0.89 },
      numberBox: { offsetXCm: 0.04 + socialValueShiftCm, yCm: 15.17, wCm: 3.59, hCm: 1.8 },
      suffixBox: { offsetXCm: 3.33 + socialValueShiftCm, yCm: 15.31, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 3.34 + socialValueShiftCm, yCm: 15.96, wCm: 4.44, hCm: 0.71 },
      leftNeighborAnchorStepCm: 4.59,
      separatorOffsetXCm: 0,
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
    {
      id: "digital",
      visible: hasData(data.digital_visitors),
      title: "DIGITAL",
      number: digitalVisitorsNum,
      suffix: digitalVisitorsSuffix,
      unit: t.VU,
      titleBox: { offsetXCm: 0, yCm: 13.23, wCm: 4.58, hCm: 0.89 },
      numberBox: { offsetXCm: -1.16 + digitalValueShiftCm, yCm: 15.17, wCm: 3.95, hCm: 1.8 },
      suffixBox: { offsetXCm: 2.45 + digitalValueShiftCm, yCm: 15.31, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 2.5 + digitalValueShiftCm, yCm: 15.96, wCm: 4.44, hCm: 1.15 },
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
  ].map((group) => ({
    ...group,
    bounds: {
      minXCm: Math.min(
        group.titleBox.offsetXCm,
        group.numberBox.offsetXCm,
        ...(group.suffix ? [group.suffixBox.offsetXCm] : []),
        group.unitBox.offsetXCm
      ),
      maxXCm: Math.max(
        group.titleBox.offsetXCm + group.titleBox.wCm,
        group.numberBox.offsetXCm + group.numberBox.wCm,
        ...(group.suffix ? [group.suffixBox.offsetXCm + group.suffixBox.wCm] : []),
        group.unitBox.offsetXCm + group.unitBox.wCm
      ),
      widthCm: 0,
    },
  })).map((group) => ({
    ...group,
    bounds: {
      ...group.bounds,
      widthCm: group.bounds.maxXCm - group.bounds.minXCm,
    },
  }));

  const metricGroups = layoutMetricGroupsByBoundsRightToLeft(
    metricGroupDefinitions,
    metricLayoutConfig
  );

  metricGroups.forEach(renderSingleMetricGroup);

  return pptx;
}
